<template>
  <div class="account-badge-wrapper">
    <div v-if="loading" class="account-badge account-badge--loading">
      <div class="skeleton-avatar"></div>
      <div class="skeleton-lines">
        <div class="skeleton-line skeleton-line--name"></div>
        <div class="skeleton-line skeleton-line--id"></div>
      </div>
      <div class="skeleton-status"></div>
    </div>
    <el-tooltip v-else-if="accountInfo" effect="dark" placement="bottom-end" :open-delay="300">
      <template #content>
        <div class="tooltip-content">
          <div class="tt-row"><span class="tt-label">企微名称</span><span class="tt-value">{{ accountInfo.userName || '-' }}</span></div>
          <div class="tt-row"><span class="tt-label">账号标识</span><span class="tt-value">{{ accountInfo.identity || '-' }}</span></div>
          <div class="tt-row"><span class="tt-label">所属企业</span><span class="tt-value">{{ accountInfo.corpName || '-' }}</span></div>
          <div class="tt-row"><span class="tt-label">VID</span><span class="tt-value">{{ accountInfo.vid || '-' }}</span></div>
          <div class="tt-divider"></div>
          <div class="tt-row"><span class="tt-label">账号状态</span><span class="tt-value" :style="{ color: statusColor }">{{ statusLabel }}</span></div>
          <div v-if="accountInfo.banReason" class="tt-ban-reason">
            <div v-if="accountInfo.banStartTime && accountInfo.banFinishTime">
              <strong>时间：</strong>{{ formatTime(accountInfo.banStartTime) }} ~ {{ formatTime(accountInfo.banFinishTime) }}
            </div>
          </div>
        </div>
      </template>
      <div class="account-badge" :class="'account-badge--' + statusClass">
        <head-image :name="accountInfo.userName" :url="accountInfo.avatar" :size="28" />
        <div class="account-info">
          <span class="account-name">{{ accountInfo.userName || '-' }}</span>
          <span class="account-identity">{{ accountInfo.identity || '-' }}</span>
        </div>
        <span class="status-dot"></span>
        <span class="status-text">{{ statusLabel }}</span>
      </div>
    </el-tooltip>
    <div v-else class="account-badge account-badge--unknown" @click.stop>
      <span class="status-text">暂无账号信息</span>
    </div>
  </div>
</template>

<script>
import HeadImage from '../common/HeadImage.vue';

const STATUS_MAP = {
  uninit:      { label: '未初始化', color: '#909399' },
  banned:      { label: '已封禁',   color: '#f56c6c' },
  limited:     { label: '限制中',   color: '#e6a23c' },
  quota:       { label: '配额超限', color: '#f56c6c' },
  frequent:    { label: '频繁加人', color: '#409eff' },
  offline:     { label: '离线',     color: '#c0c4cc' },
  online:      { label: '在线',     color: '#67c23a' },
};

export default {
  name: 'AccountBadge',
  components: { HeadImage },
  props: {
    accountInfo: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    statusKey() {
      const s = this.accountInfo;
      if (!s) return 'offline';
      if (!s.initialed) return 'uninit';
      if (s.accountStatus === '封禁') return 'banned';
      if (s.accountStatus === '限制') return 'limited';
      if (s.accountStatus === '配额超限') return 'quota';
      if (s.accountStatus === '频繁加人') return 'frequent';
      if (!s.login) return 'offline';
      return 'online';
    },
    statusLabel() {
      return STATUS_MAP[this.statusKey]?.label || '未知';
    },
    statusColor() {
      return STATUS_MAP[this.statusKey]?.color || '#909399';
    },
    statusClass() {
      return this.statusKey;
    }
  },
  methods: {
    formatTime(ts) {
      if (!ts) return '';
      const d = new Date(ts * 1000);
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.account-badge-wrapper {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.account-badge {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 2px 10px 2px 5px;
  border-radius: 20px;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  cursor: pointer;
  transition: background 0.2s;
  max-width: 220px;

  &:hover {
    background: #ecf0f5;
  }

  .account-info {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    min-width: 0;
    overflow: hidden;
  }

  .account-name {
    font-size: 13px;
    font-weight: 500;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .account-identity {
    font-size: 11px;
    color: #909399;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-left: 2px;
  }

  .status-text {
    font-size: 11px;
    flex-shrink: 0;
    white-space: nowrap;
  }
}

// 各状态颜色
.account-badge--online   .status-dot { background: #67c23a; } .status-text { color: #67c23a; }
.account-badge--offline  .status-dot { background: #c0c4cc; } .status-text { color: #c0c4cc; }
.account-badge--banned   .status-dot { background: #f56c6c; } .status-text { color: #f56c6c; }
.account-badge--limited  .status-dot { background: #e6a23c; } .status-text { color: #e6a23c; }
.account-badge--quota    .status-dot { background: #f56c6c; } .status-text { color: #f56c6c; }
.account-badge--frequent .status-dot { background: #409eff; } .status-text { color: #409eff; }
.account-badge--uninit   .status-dot { background: #909399; } .status-text { color: #909399; }

// Unknown state fallback
.account-badge--unknown {
  padding: 2px 12px;
  cursor: default;
  .status-text { color: #909399; font-size: 12px; }
}

// Loading skeleton
.account-badge--loading {
  gap: 9px;
  padding: 3px 12px;
  cursor: default;

  .skeleton-avatar {
    width: 26px; height: 26px;
    border-radius: 50%;
    background: #e8e8e8;
    flex-shrink: 0;
  }

  .skeleton-lines {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .skeleton-line {
    height: 10px;
    border-radius: 4px;
    background: #e8e8e8;

    &--name { width: 50px; }
    &--id   { width: 70px; }
  }

  .skeleton-status {
    width: 32px; height: 10px;
    border-radius: 4px;
    background: #e8e8e8;
  }
}

// Tooltip content
.tooltip-content {
  font-size: 12px;
  line-height: 1.8;
  min-width: 200px;

  .tt-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
  }

  .tt-label {
    color: #c0c4cc;
    flex-shrink: 0;
  }

  .tt-value {
    color: #fff;
    text-align: right;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .tt-divider {
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    margin: 4px 0;
  }

  .tt-ban-reason {
    margin-top: 6px;
    padding: 6px 8px;
    background: rgba(245, 108, 108, 0.15);
    border-radius: 3px;
    color: #f56c6c;
    font-size: 11px;
    line-height: 1.6;
  }
}
</style>
